import Cookies from "js-cookie";
import type { ParsedUrlQuery } from "querystring";
import { searchParamKeys, type StoredClickIds } from "./analyticsParams";
import { routeCookieName } from "@/constants";

export const clickIdsCookieName = "click-ids";
export const noAdsCookieName = "noa";

/** Attribution window, in days. */
const ATTRIBUTION_EXPIRY = 90;

const KNOWN_ROUTES: VisitRoute[] = [
	"direct", "google", "organic", "unknown", "facebook", "twitter",
	"tiktok", "instagram", "bing", "apple", "email", "push", "social",
];

/**
 * Reads a single value out of a Pages Router query, which — unlike the App Router's
 * `URLSearchParams` — yields `string[]` for repeated params.
 */
export const firstParam = (query: ParsedUrlQuery, key: string): string | undefined => {
	const value = query[key];
	return Array.isArray(value) ? value[0] : value;
};

export const isVisitRoute = (value: string | undefined): value is VisitRoute =>
	!!value && KNOWN_ROUTES.includes(value as VisitRoute);

/**
 * Identifies the acquisition channel from the landing URL, or null when the visit
 * carries no attribution signal at all.
 */
export const detectRouteFromQuery = (query: ParsedUrlQuery): VisitRoute | null => {
	const utmSource = firstParam(query, "utm_source")?.trim().toLowerCase();

	if (firstParam(query, "gclid") || utmSource === "google") return "google";
	if (firstParam(query, "msclkid")) return "bing";
	if (firstParam(query, "fbclid") || utmSource === "fb" || utmSource === "ig") return "facebook";
	if (firstParam(query, "twclid")) return "twitter";
	if (firstParam(query, "ttclid")) return "tiktok";
	if (firstParam(query, "adid")) return "apple";

	const utmMedium = firstParam(query, "utm_medium")?.trim().toLowerCase();
	if (utmMedium === "email" || utmSource === "email") return "email";

	return null;
};

export const findRouteNameFromQuery = (query: ParsedUrlQuery): VisitRoute =>
	detectRouteFromQuery(query) ?? "organic";

/**
 * Resolves the visit route on a last-touch basis and persists it.
 *
 * Runs on every visit, not just the first: a returning user arriving from a new paid
 * click must be re-attributed. The stored route is only overwritten on a real signal,
 * so an organic return visit never downgrades a previously attributed non-organic route.
 */
export const resolveRoute = (query: ParsedUrlQuery): VisitRoute => {
	const detected = detectRouteFromQuery(query);
	const storedRaw = Cookies.get(routeCookieName);
	const stored = isVisitRoute(storedRaw) ? storedRaw : undefined;
	const route = detected ?? stored ?? "organic";

	// Persist on a real signal, or to seed when nothing valid is stored yet.
	if (detected || !stored) {
		Cookies.set(routeCookieName, route, { expires: ATTRIBUTION_EXPIRY });
	}

	return route;
};

export const readClickIds = (): StoredClickIds => {
	try {
		return JSON.parse(Cookies.get(clickIdsCookieName) ?? "{}") as StoredClickIds;
	} catch {
		return {};
	}
};

/**
 * Merges the current URL's click ids over the stored set and persists the result.
 * Merging (rather than replacing) keeps ids captured on an earlier landing alive when
 * the user re-enters through a URL that carries only some of them.
 */
export const captureClickIds = (query: ParsedUrlQuery): StoredClickIds => {
	const stored = readClickIds();

	const merged = searchParamKeys.reduce((acc, key) => {
		const value = firstParam(query, key) || stored[key];
		if (value) acc[key] = value;
		return acc;
	}, {} as StoredClickIds);

	Cookies.set(clickIdsCookieName, JSON.stringify(merged), { expires: ATTRIBUTION_EXPIRY });

	return merged;
};
